import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronRight, Loader2, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  getPatientsByReferringDoctor,
  getPatientStageTimeline,
  type ReferringDoctorWithStats,
} from '@/services/referringDirectoryService';
import PatientVisitHistory from '@/components/PatientVisitHistory';
import { treatmentStatusMeta, STAGE_LABEL } from '@/lib/treatmentStatus';

interface ReferringDoctorPatientsDialogProps {
  doctor: ReferringDoctorWithStats | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatSeen = (value: string | null): string => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'd MMM yyyy');
  } catch {
    return value;
  }
};

// Coarse treatment status + stage labels come from the shared source of truth so
// the dentist-facing badge stays consistent (and recognises the terminal
// app-side statuses completed / referred_back / inactive, not just Plato's).

const StageTimeline: React.FC<{ platoPatientId: string }> = ({ platoPatientId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['patient-stage-timeline', platoPatientId],
    queryFn: () => getPatientStageTimeline(platoPatientId),
  });
  const rows = (data ?? []).filter((r) => r.stage !== 'other');

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading stages…
      </div>
    );
  }
  if (rows.length === 0) {
    return <p className="py-1 text-xs text-muted-foreground">No treatment stages captured yet.</p>;
  }
  return (
    <div className="mb-3 space-y-1">
      <p className="text-xs font-medium text-slate-600">Treatment stages</p>
      {rows.map((r, i) => (
        <div key={`${r.stage_date}-${i}`} className="flex items-baseline gap-2 text-xs">
          <span className="w-20 shrink-0 tabular-nums text-slate-500">{formatSeen(r.stage_date)}</span>
          <span className="font-medium text-slate-800">{STAGE_LABEL[r.stage] ?? r.stage}</span>
          <span className="truncate text-slate-400">{r.description}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Modal listing every patient a referring dentist sent (from the Plato
 * "Referred By" mapping), each with a coarse treatment status. Clicking a
 * patient expands in place to the internal detail — the endodontic stage
 * timeline + Plato visit history.
 */
const ReferringDoctorPatientsDialog: React.FC<ReferringDoctorPatientsDialogProps> = ({
  doctor,
  open,
  onOpenChange,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const patientsQuery = useQuery({
    queryKey: ['referring-doctor-patients', doctor?.id],
    queryFn: () => getPatientsByReferringDoctor(doctor!.id),
    enabled: open && Boolean(doctor?.id),
  });

  const patients = patientsQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-slate-500" />
            {doctor?.name ?? 'Referring dentist'}
          </DialogTitle>
          <DialogDescription>
            {doctor?.clinic?.name ? `${doctor.clinic.name} — ` : ''}
            patients referred to Twin City. Click a patient to see the treatment detail.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {patientsQuery.isLoading && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading patients…
            </div>
          )}

          {patientsQuery.isError && (
            <p className="py-4 text-sm text-rose-700">
              {patientsQuery.error instanceof Error
                ? patientsQuery.error.message
                : 'Could not load patients.'}
            </p>
          )}

          {!patientsQuery.isLoading && !patientsQuery.isError && patients.length === 0 && (
            <p className="py-6 text-sm text-muted-foreground">
              No patients mapped to this dentist yet.
            </p>
          )}

          {!patientsQuery.isLoading && patients.length > 0 && (
            <div className="space-y-1">
              <p className="px-1 pb-1 text-xs text-muted-foreground">
                {patients.length} patient{patients.length === 1 ? '' : 's'}
              </p>
              {patients.map((patient) => {
                const isOpen = expandedId === patient.plato_patient_id;
                const seen = formatSeen(patient.last_seen);
                const treat = treatmentStatusMeta(patient.treatment_status);
                const stage =
                  patient.current_stage && patient.current_stage !== 'other'
                    ? STAGE_LABEL[patient.current_stage] ?? patient.current_stage
                    : null;
                return (
                  <div
                    key={patient.plato_patient_id}
                    className="rounded-lg border border-slate-100 bg-white"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedId(isOpen ? null : patient.plato_patient_id)
                      }
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50',
                        isOpen && 'bg-slate-50',
                      )}
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                      )}
                      <span className="flex-1 text-sm font-medium text-slate-900">
                        {patient.patient_name ?? 'Unknown patient'}
                      </span>
                      <Badge
                        variant="outline"
                        className={cn('text-[11px] font-normal', treat.className)}
                      >
                        {treat.label}
                      </Badge>
                      {stage && (
                        <span className="hidden text-xs text-muted-foreground sm:inline">
                          {stage}
                        </span>
                      )}
                    </button>

                    {isOpen && (
                      <div className="border-t border-slate-100 px-3 py-3">
                        {seen && (
                          <p className="mb-2 text-xs text-muted-foreground">
                            Last seen {seen}
                          </p>
                        )}
                        <StageTimeline platoPatientId={patient.plato_patient_id} />
                        <PatientVisitHistory platoReferenceId={patient.plato_patient_id} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReferringDoctorPatientsDialog;
